import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import FormNumberField from "../../components/formComponents/FormNumberField";
import FormSwitch from "../../components/formComponents/FormSwitch";
import { Select } from "../../components/select/Select";

interface DialogProps {
  readonly open: boolean;
  readonly action: string;
  readonly description: string;
  readonly config: ConfigFormState;
  readonly onChange: (config: ConfigFormState) => Promise<void>;
  readonly onClose: () => void;
}

export interface ConfigFormState {
  readonly staticConfig: StaticConfig | null;
  readonly dynamicConfig: DynamicConfig | null;
  readonly enabled: boolean;
}

export interface StaticConfig {
  points: number;
}

export interface DynamicConfig {
  multiplier: number;
}

const configurationTypes = [
  { value: "static", label: "statyczny" },
  { value: "dynamic", label: "dynamiczny" },
];

const EditLoyaltyConfig: FC<DialogProps> = ({ open, action, description, config, onChange, onClose }) => {
  const { control, handleSubmit, watch, setValue } = useForm<ConfigFormState>({
    defaultValues: config,
    mode: "onBlur",
  });
  const state = watch();

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6">Edytuj {action}</Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {description}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            marginTop: 2,
          }}
        >
          <Select
            label="Rodzaj konfiguracji"
            fullWidth
            values={configurationTypes}
            value={state.staticConfig ? "static" : "dynamic"}
            onSelect={(e) => {
              if (e === "static") {
                setValue("staticConfig", { points: 0 });
                setValue("dynamicConfig", null);
              } else {
                setValue("dynamicConfig", { multiplier: 1 });
                setValue("staticConfig", null);
              }
            }}
          />
          {state.staticConfig && (
            <FormNumberField
              control={control}
              label="Punkty"
              name="staticConfig.points"
              min={0}
              rules={{
                min: { value: 0, message: "Punkty nie mogą być ujemne" },
                validate: (value) => {
                  if (value === null || value === undefined) return true;
                  return Number.isInteger(Number(value)) || "Musi być liczbą całkowitą";
                },
              }}
            />
          )}
          {state.dynamicConfig && (
            <FormNumberField
              control={control}
              label="Mnożnik"
              name="dynamicConfig.multiplier"
              min={0.01}
              rules={{
                min: { value: 0.01, message: "Minimalny mnożnik musi być większy od zera" },
              }}
            />
          )}
          <FormSwitch control={control} name="enabled" label="Włączony" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} />
        <AsyncLoadingButton
          disabled={isEqual(state, config)}
          onClick={handleSubmit(async (data) => {
            await onChange(data);
            onClose();
          })}
        >
          Zapisz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditLoyaltyConfig;
